
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import type { Json } from "@/integrations/supabase/types";
import { parseFAQs } from "./types/contact-widget";
import { motion } from "framer-motion";
import { HelpCircle, Plus } from "lucide-react";

interface FAQAccordionProps {
  faqs?: Json;
}

export const FAQAccordion = ({ faqs }: FAQAccordionProps) => {
  const faqItems = parseFAQs(faqs);

  if (!faqItems.length) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="p-6 space-y-6"
    >
      <motion.div 
        className="flex items-center gap-3 mb-6"
        initial={{ x: -20 }}
        animate={{ x: 0 }}
        transition={{ delay: 0.2 }}
      >
        <div className="p-2 rounded-full bg-gradient-to-br from-blue-100 to-purple-100">
          <HelpCircle className="w-5 h-5 text-blue-600" />
        </div>
        <h3 className="text-lg font-semibold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
          Frequently Asked Questions
        </h3>
      </motion.div>

      <Accordion type="single" collapsible className="w-full space-y-3">
        {faqItems.map((faq, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 * (index + 1) }}
          >
            <AccordionItem 
              value={`item-${index}`}
              className="group border border-blue-100 rounded-lg px-4 data-[state=open]:bg-gradient-to-br data-[state=open]:from-blue-50/50 data-[state=open]:to-purple-50/50 data-[state=open]:shadow-sm transition-all duration-300 hover:border-blue-200"
            >
              <AccordionTrigger className="text-left py-4 hover:no-underline">
                <div className="flex items-start gap-3 group">
                  <div className="shrink-0 mt-1">
                    <div className="p-1.5 rounded-full bg-gradient-to-br from-blue-100/80 to-purple-100/80 group-hover:from-blue-200/80 group-hover:to-purple-200/80 transition-colors duration-300">
                      <Plus className="w-3.5 h-3.5 text-blue-600 group-data-[state=open]:rotate-45 transition-transform duration-200" />
                    </div>
                  </div>
                  <span className="text-base font-medium text-gray-700 group-hover:text-gray-900 transition-colors duration-200">
                    {faq.question}
                  </span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pl-[2.25rem] pb-4">
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                  className="text-gray-600 text-[15px] leading-relaxed"
                >
                  {faq.answer}
                </motion.div>
              </AccordionContent>
            </AccordionItem>
          </motion.div>
        ))}
      </Accordion>
    </motion.div>
  );
};
